/**
 * LLS-569 item 2: Phone Type on the Create Account step.
 *
 * The type is a UI-only choice (not persisted). It decides which of the borrower's three phone
 * slots the single "Phone Number" input writes to. Pure functions so the routing rules are
 * unit-testable without Angular.
 */
export type PhoneType = 'Mobile' | 'Home' | 'Work';

export const PHONE_TYPES: readonly PhoneType[] = ['Mobile', 'Home', 'Work'];

export interface PhoneSlots {
  mobilePhone: string;
  homePhone: string;
  workPhone: string;
}

const SLOT_FOR: Record<PhoneType, keyof PhoneSlots> = {
  Mobile: 'mobilePhone',
  Home: 'homePhone',
  Work: 'workPhone',
};

/** On resume the type is whichever slot is populated, mobile > home > work; Mobile when none is. */
export function derivePhoneType(borrower: PhoneSlots | null | undefined): PhoneType {
  if (!borrower) {
    return 'Mobile';
  }
  if (borrower.mobilePhone) {
    return 'Mobile';
  }
  if (borrower.homePhone) {
    return 'Home';
  }
  if (borrower.workPhone) {
    return 'Work';
  }
  return 'Mobile';
}

/** The number currently held in the slot for `type`; always a string. */
export function phoneNumberFor(borrower: PhoneSlots | null | undefined, type: PhoneType): string {
  return borrower?.[SLOT_FOR[type]] ?? '';
}

/**
 * Routes `number` into the slot for `type` and clears the other slots.
 *
 * Mobile keeps the TFS 13121 / PR 11693 behaviour ("enter the online-app phone into both cell and
 * home in the URLA"), softened the same way My Info did: homePhone mirrors the mobile number only
 * while it is empty or is itself the previous mirror value, so a home phone the borrower entered
 * separately is never overwritten. Home and Work never copy anywhere.
 */
export function applyPhoneNumber(borrower: PhoneSlots, type: PhoneType, number: string | null | undefined): void {
  const value = number ?? '';
  if (type === 'Mobile') {
    const previousMobile = borrower.mobilePhone ?? '';
    const homeIsMirror = !borrower.homePhone || borrower.homePhone === previousMobile;
    borrower.mobilePhone = value;
    borrower.workPhone = '';
    if (homeIsMirror) {
      borrower.homePhone = value;
    }
    return;
  }
  borrower.mobilePhone = '';
  borrower.homePhone = '';
  borrower.workPhone = '';
  borrower[SLOT_FOR[type]] = value;
}
